import React, { useState, useCallback } from 'react';
import { Play, GitCompare, AlertCircle } from 'lucide-react';
import toolkitService from '../services/ToolkitService';
import serverRead from './toolkit/serverRead';
import Button from './shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import ToolkitFileStep, { STEP, stepIcon as iconFor } from './toolkit/ToolkitFileStep';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › Blocks.
//
// Ported from the desktop toolkit's Blocks comparison: an Opera v5 Block Information report
// (resblkinfo.rep) against the same blocks in OPERA Cloud, every date x room type compared on
// allocation, pickup, availability, rates and revenue.
//
// THREE EXPLICIT STEPS rather than one button. Each is separately slow and separately fallible — the
// upload is bounded by the file, the cloud fetch by OHIP's rate limit (two calls per block, and the
// lambda is pinned to one instance), the compare by neither. Collapsing them would mean a single spinner
// covering minutes of work with no way to tell which part is running, and a failure in the third step
// discarding the first two. Keeping the results of each also makes the compare re-runnable with
// different options without paying for the fetch again.
//
// The OPERA Cloud side is the sync project's own target connection (its property) — nothing to choose here.


const todayIso = () => new Date().toISOString().slice(0, 10);
const monthsFrom = (n) => {
    const d = new Date();
    d.setUTCMonth(d.getUTCMonth() + n);
    return d.toISOString().slice(0, 10);
};

function ToolkitBlocksPage() {
    const toolkit = useToolkitProject();
    const { ctx, ready } = toolkit;
    const [fromDate, setFromDate] = useState(todayIso());
    const [toDate, setToDate] = useState(monthsFrom(12));
    const [includeInit, setIncludeInit] = useState(true);
    const [cutoverDate, setCutoverDate] = useState('');
    const [dateFormat, setDateFormat] = useState('dd-MM-yy');

    const [v5, setV5] = useState({ data: null });
    const [cloud, setCloud] = useState({ state: STEP.IDLE, data: null, message: '', failures: [] });
    const [result, setResult] = useState({ state: STEP.IDLE, data: null, message: '' });

    // The v5 report — uploaded and parsed by the shared file step; the block parser has its own route.
    const parseBlocks = useCallback(async (key) => {
        const { data } = await toolkitService.blocks.parse(key);
        return { data: data.data, message: `${data.blocks} block(s), ${data.dates} date row(s)` };
    }, []);
    const onParsed = useCallback((data) => setV5({ data }), []);

    const fetchCloud = async () => {
        if (!ready) return;
        setCloud({ state: STEP.BUSY, data: null, message: 'Fetching from OPERA Cloud — two calls per block, rate limited…', failures: [] });
        try {
            // On the server (a READ job): two calls per block runs well past API Gateway's 29 seconds for a busy property.
            const data = await serverRead(ctx, { area: 'tasks', op: 'cloud-blocks', screen: 'blocks', title: 'Opera Cloud blocks',
                args: {
                    fromDate, toDate,
                    // Only fetch what we are going to compare, when we already know the codes.
                    blockCodes: v5.data ? v5.data.map(b => b.allotmentCode) : null,
                },
                onStatus: (m) => setCloud((c) => ({ ...c, message: `Fetching from OPERA Cloud — two calls per block, rate limited. ${m}` })) });
            setCloud({
                state: STEP.DONE, data: data.data, failures: data.failures || [],
                message: `${data.fetched} of ${data.listed} block(s) in ${(data.tookMs / 1000).toFixed(1)}s`,
            });
        } catch (err) {
            setCloud({ state: STEP.ERROR, data: null, failures: [],
                       message: err?.response?.data?.message || err.message || 'Fetch failed' });
        }
    };

    const compare = async () => {
        setResult({ state: STEP.BUSY, data: null, message: 'Comparing…' });
        try {
            const { data } = await toolkitService.blocks.compare({
                v5Data: v5.data, cloudData: cloud.data,
                includeInit,
                cutoverDate: cutoverDate || null,
                hotelDateFormat: dateFormat || null,
            });
            setResult({ state: STEP.DONE, data, message: '' });
        } catch (err) {
            setResult({ state: STEP.ERROR, data: null,
                        message: err?.response?.data?.message || err.message || 'Compare failed' });
        }
    };

    const exportCsv = () => {
        if (!result.data) return;
        const head = ['blockCode', 'status', 'date', 'roomType', 'field', 'v5', 'oc'];
        const rows = [];
        for (const r of result.data.results) {
            if (!r.differences.length) { rows.push([r.blockCode, r.status, '', '', '', '', '']); continue; }
            for (const d of r.differences) rows.push([r.blockCode, r.status, d.date, d.roomType, d.field, d.v5, d.oc]);
        }
        const csv = [head.join(','), ...rows.map(r => r.map(c => `"${String(c ?? '')}"`).join(','))].join('\n');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        a.download = `block-comparison-${fromDate}_${toDate}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const stepIcon = (s) => iconFor(s.state);

    const summary = result.data && result.data.summary;


    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="Blocks"
                subtitle="Compare an Opera v5 Block Information report (resblkinfo.rep) against the same blocks in OPERA Cloud — every date and room type, on allocation, pickup, availability, rates and revenue." />

            {/* 2 — v5 report */}
            <ToolkitFileStep number={1} title="Opera v5 report" label="Choose resblkinfo.rep / XML"
                parse={parseBlocks} onParsed={onParsed} />

            {/* 3 — cloud */}
            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">2</span> OPERA Cloud blocks {stepIcon(cloud)}</div>
                <div className="tkb-row">
                    <label><span>From</span><input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} /></label>
                    <label><span>To</span><input type="date" value={toDate} onChange={e => setToDate(e.target.value)} /></label>
                    <Button variant="secondary" onClick={fetchCloud} disabled={cloud.state === STEP.BUSY || !ready}>
                        <Play className="icon" /> Fetch
                    </Button>
                </div>
                {cloud.message && <p className={`tkb-msg${cloud.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{cloud.message}</p>}
                {/* Blocks that could not be read are named, so a short comparison never looks complete. */}
                {!!cloud.failures.length && (
                    <div className="tkb-warn">
                        <AlertCircle size={14} /> {cloud.failures.length} block(s) could not be read and are
                        NOT in the comparison: {cloud.failures.slice(0, 8).map(f => f.blockCode).join(', ')}
                        {cloud.failures.length > 8 ? ` and ${cloud.failures.length - 8} more` : ''}
                    </div>
                )}
            </div>

            {/* 4 — compare */}
            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">3</span> Compare {stepIcon(result)}</div>
                <div className="tkb-row">
                    <label className="tkb-check">
                        <input type="checkbox" checked={includeInit} onChange={e => setIncludeInit(e.target.checked)} />
                        <span>Include INIT</span>
                    </label>
                    <label>
                        <span>Cutover date</span>
                        <input type="date" value={cutoverDate} onChange={e => setCutoverDate(e.target.value)} />
                    </label>
                    <label>
                        <span>Hotel date format</span>
                        <select value={dateFormat} onChange={e => setDateFormat(e.target.value)}>
                            <option value="dd-MM-yy">dd-MM-yy (day first)</option>
                            <option value="MM-dd-yy">MM-dd-yy (month first)</option>
                        </select>
                    </label>
                    <Button variant="success" onClick={compare}
                            disabled={result.state === STEP.BUSY || !v5.data || !cloud.data}>
                        <GitCompare className="icon" /> Compare
                    </Button>
                </div>
                {/* The format only disambiguates numeric dates; named months are read directly. */}
                <p className="tkb-hint">
                    Dates before the cutover are skipped — they were never migrated, so a difference there
                    is expected. The date format only matters for numeric dates like 07-01-26.
                </p>
                {result.message && <p className={`tkb-msg${result.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{result.message}</p>}
            </div>

            {summary && (
                <>
                    <div className="otx-stats">
                        <div className="otx-stat"><span className="otx-stat-label">Matched</span><span className="otx-stat-value otx-ok">{summary.matched}</span></div>
                        <div className="otx-stat"><span className="otx-stat-label">With differences</span><span className="otx-stat-value tkb-warn-text">{summary.partial}</span></div>
                        <div className="otx-stat"><span className="otx-stat-label">Missing in Cloud</span><span className="otx-stat-value otx-fail">{summary.missing}</span></div>
                        <div className="otx-stat"><span className="otx-stat-label">Lines compared</span><span className="otx-stat-value">{summary.linesCompared}</span></div>
                        <div className="otx-stat"><span className="otx-stat-label">Skipped (pre-cutover)</span><span className="otx-stat-value">{summary.linesSkipped}</span></div>
                    </div>

                    <div className="otx-header" style={{ marginBottom: '0.5rem' }}>
                        <div />
                        <Button variant="secondary" onClick={exportCsv}>Export CSV</Button>
                    </div>

                    <div className="otx-table-wrap">
                        <table className="otx-table">
                            <thead>
                                <tr><th>Block</th><th>Status</th><th>Date</th><th>Room type</th><th>Field</th><th>v5</th><th>Cloud</th></tr>
                            </thead>
                            <tbody>
                                {result.data.results.map(r => (
                                    r.differences.length ? r.differences.map((d, i) => (
                                        <tr key={`${r.blockCode}-${i}`}>
                                            {i === 0 && <td rowSpan={r.differences.length}><strong>{r.blockCode}</strong><div className="tkb-desc">{r.v5Description}</div></td>}
                                            {i === 0 && <td rowSpan={r.differences.length}><span className="tkb-badge tkb-badge-partial">{r.statusText}</span></td>}
                                            <td>{d.date}</td><td>{d.roomType}</td><td>{d.field}</td>
                                            <td className="otx-numeric">{String(d.v5)}</td>
                                            <td className="otx-numeric otx-fail">{String(d.oc)}</td>
                                        </tr>
                                    )) : (
                                        <tr key={r.blockCode}>
                                            <td><strong>{r.blockCode}</strong><div className="tkb-desc">{r.v5Description}</div></td>
                                            <td><span className={`tkb-badge tkb-badge-${r.status}`}>{r.statusText}</span></td>
                                            <td colSpan={5} className="otx-empty" style={{ padding: '0.5rem' }}>
                                                {r.status === 'missing' ? 'Not present in OPERA Cloud' : 'No differences'}
                                            </td>
                                        </tr>
                                    )
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

export default ToolkitBlocksPage;
